import { ChildData } from "../types";
import { StepStatus } from "../lib/hooks/useFormFlow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faCheck } from "@fortawesome/free-solid-svg-icons";

interface HeaderProps {
  childData: ChildData | null;
  status?: StepStatus;
  furthestStatus?: StepStatus;
  isDesktop?: boolean;
  onStatusClick?: (clickedStatus: "start" | "photo" | "message" | "sent") => void;
}

/**
 * Header component that displays child information and application flow progress
 * - Shows a step indicator that visualizes the user's progress through the application
 * - Adapts layout for desktop/mobile views with different visual treatments
 */
function Header({
  childData,
  status = "start",
  furthestStatus = "start",
  isDesktop = false,
  onStatusClick
}: HeaderProps) {
  const childName = childData?.givenName || "";
  const countryName = childData?.countryName || "";
  const childPhotoUrl = childData?.cgpUrl || "";

  // Determine which step is currently active based on the application status
  const isStartActive = status === "start";
  const isPhotoActive = status.startsWith("photo-");
  const isMessageActive = status.startsWith("questionnaire-");
  const isSentActive = status === "sent";

  // Determine which steps are completed based on the current status
  // This is used to show checkmarks instead of numbers on completed steps in desktop view
  const isStartComplete = isPhotoActive || isMessageActive || isSentActive;
  const isPhotoComplete = isMessageActive || isSentActive;
  const isMessageComplete = isSentActive;

  // Determine which steps the user has reached based on furthestStatus
  const hasReachedPhoto =
    furthestStatus.startsWith("photo-") ||
    furthestStatus.startsWith("questionnaire-") ||
    furthestStatus === "sent";
  const hasReachedMessage =
    furthestStatus.startsWith("questionnaire-") || furthestStatus === "sent";

  // Determine clickability based on status and furthestStatus
  // No indicators should be clickable when on sent status (except sent itself for re-animation)
  const notInSentStatus = status !== "sent";
  const isInSentStatus = status === "sent";
  const canClickStart = notInSentStatus; // Clickable as long as not in sent status
  const canClickPhoto = notInSentStatus && hasReachedPhoto;
  const canClickMessage = notInSentStatus && hasReachedMessage;
  // Sent should only be clickable when already in sent status
  const canClickSent = isInSentStatus;

  /**
   * Handle click on a status indicator
   * Follows the rules:
   * - Start: Clickable when not in sent status
   * - Photo: Clickable if user has reached photo and not in sent status
   * - Message: Clickable if user has reached message and not in sent status
   * - Sent: Only clickable when already on sent status (reanimates the card preview)
   */
  const handleStatusClick = (clickedStatus: "start" | "photo" | "message" | "sent") => {
    if (!onStatusClick) return;

    onStatusClick(clickedStatus);
  };

  return (
    <header className="header">
      <div className="header-title-container">
        <div className="header-title-content">
          {isDesktop && (
            <>
              <span>Create a card for your sponsored child</span>
              <div className="vertical-divider"></div>
            </>
          )}
          {childName && countryName ? (
            <>
              {childName} in {countryName}
            </>
          ) : (
            <>&nbsp;</>
          )}
        </div>
      </div>
      <div className="header-status-container">
        <div className="header-status-content">
          <div
            className={`status-indicator ${isStartActive ? "active" : ""} ${canClickStart ? "clickable" : ""}`}
            onClick={() => canClickStart && handleStatusClick("start")}
          >
            {isDesktop && (
              <span className={isStartComplete ? "step-check" : "step-number"}>
                {isStartComplete ? <FontAwesomeIcon icon={faCheck} /> : "1."}
              </span>
            )}
            Start
          </div>
          <div
            className={`status-indicator ${isPhotoActive ? "active" : ""} ${canClickPhoto ? "clickable" : ""}`}
            onClick={() => canClickPhoto && handleStatusClick("photo")}
          >
            {isDesktop && (
              <span className={isPhotoComplete ? "step-check" : "step-number"}>
                {isPhotoComplete ? <FontAwesomeIcon icon={faCheck} /> : "2."}
              </span>
            )}
            Photo
          </div>
          <div
            className={`status-indicator ${isMessageActive ? "active" : ""} ${canClickMessage ? "clickable" : ""}`}
            onClick={() => canClickMessage && handleStatusClick("message")}
          >
            {isDesktop && (
              <span className={isMessageComplete ? "step-check" : "step-number"}>
                {isMessageComplete ? <FontAwesomeIcon icon={faCheck} /> : "3."}
              </span>
            )}
            Message
          </div>
          <div
            className={`status-indicator sent-indicator ${isSentActive ? "active" : ""} ${canClickSent ? "clickable" : ""}`}
            onClick={() => canClickSent && handleStatusClick("sent")}
          >
            {isDesktop && (
              <span className={isSentActive ? "step-star" : "step-number"}>
                {isSentActive ? <FontAwesomeIcon icon={faStar} /> : "4."}
              </span>
            )}
            Sent
            {childPhotoUrl && (
              <div
                className="child-photo"
                style={{ backgroundImage: `url(${childPhotoUrl})` }}
              ></div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
